import { useEffect, useRef } from "react";
import { EditorState, Extension } from "@codemirror/state";
import {
  EditorView,
  keymap,
  drawSelection,
  highlightActiveLine,
  highlightActiveLineGutter,
  placeholder as placeholderExtension,
} from "@codemirror/view";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { python, localCompletionSource } from "@codemirror/lang-python";
import { syntaxHighlighting, HighlightStyle } from "@codemirror/language";
import { tags } from "@lezer/highlight";
import {
  autocompletion,
  CompletionContext,
  CompletionResult,
  Completion,
  acceptCompletion,
  closeBrackets,
  closeBracketsKeymap,
  startCompletion,
} from "@codemirror/autocomplete";

// Type definitions
export type ExpressionEditorType =
  "signal" | "alert" | "opportunity" | "recommendation";

export interface AutocompleteField {
  name: string;
  type: string;
  description?: string;
}

interface ExpressionEditorProps {
  value: string;
  onChange: (value: string) => void;
  type: ExpressionEditorType;
  selectedDatasets?: string[];
  availableDatasets?: Record<string, AutocompleteField[]>;
  availableSignals?: AutocompleteField[];
  placeholder?: string;
  minHeight?: string;
}

// Create minimal Python keyword completions (just the essentials)
function minimalPythonKeywords(
  context: CompletionContext,
): CompletionResult | null {
  const word = context.matchBefore(/\w*/);
  if (!word || (word.from === word.to && !context.explicit)) return null;

  // Don't suggest keywords if we're typing after a dot (field access context)
  const charBeforeWord =
    word.from > 0
      ? context.state.doc.sliceString(word.from - 1, word.from)
      : "";
  if (charBeforeWord === ".") return null;

  const keywords = [
    {
      label: "if",
      type: "keyword",
      info: "Conditional expression (use: value_if_true if condition else value_if_false)",
    },
    { label: "else", type: "keyword", info: "Conditional alternative" },
    { label: "True", type: "keyword", info: "Boolean true" },
    { label: "False", type: "keyword", info: "Boolean false" },
    { label: "None", type: "keyword", info: "Null value" },
    { label: "and", type: "keyword", info: "Logical AND" },
    { label: "or", type: "keyword", info: "Logical OR" },
    { label: "not", type: "keyword", info: "Logical NOT" },
    { label: "in", type: "keyword", info: "Membership test" },
  ];

  const options = keywords.filter((k) => k.label.startsWith(word.text));
  if (options.length === 0) return null;

  return {
    from: word.from,
    options,
  };
}

// Create autocomplete completions
function createAutocompleteFunction(
  type: ExpressionEditorType,
  selectedDatasets: string[],
  availableDatasets: Record<string, AutocompleteField[]>,
  availableSignals: AutocompleteField[],
): (context: CompletionContext) => CompletionResult | null {
  return (context: CompletionContext) => {
    // Match word boundaries for dataset/signal names
    const word = context.matchBefore(/[\w]+\.?[\w]*/);
    if (!word) return null;
    if (word.from === word.to && !context.explicit) return null;

    const text = word.text;
    const completions: Completion[] = [];

    // Check if we're typing after a dot (e.g., "usage_metrics.")
    const afterDotMatch = text.match(/^([a-z_]+)\.(.*)$/);
    if (afterDotMatch) {
      const namespace = afterDotMatch[1];
      const fieldPrefix = afterDotMatch[2];

      // Check if this is a valid selected dataset
      if (
        selectedDatasets.includes(namespace) &&
        availableDatasets[namespace]
      ) {
        const datasetFields = availableDatasets[namespace];

        datasetFields.forEach((field) => {
          if (field.name.startsWith(fieldPrefix)) {
            completions.push({
              label: field.name,
              type: "property",
              detail: field.type,
              info: field.description,
              apply: field.name,
            });
          }
        });

        if (completions.length > 0) {
          return {
            from: word.from + namespace.length + 1, // Position after the dot
            options: completions,
          };
        }
      }

      // Check if this is "signals."
      if (
        namespace === "signals" &&
        (type === "alert" ||
          type === "opportunity" ||
          type === "recommendation")
      ) {
        availableSignals.forEach((field) => {
          if (field.name.startsWith(fieldPrefix)) {
            completions.push({
              label: field.name,
              type: "property",
              detail: field.type,
              info: field.description,
              apply: field.name,
            });
          }
        });

        if (completions.length > 0) {
          return {
            from: word.from + namespace.length + 1, // Position after the dot
            options: completions,
          };
        }
      }
    }

    // If not after a dot, check if we're typing a dataset/signal namespace
    if (!text.includes(".")) {
      // Add dataset namespaces for signals and alerts
      if (
        (type === "signal" || type === "alert") &&
        selectedDatasets.length > 0
      ) {
        selectedDatasets.forEach((datasetId) => {
          if (datasetId.startsWith(text)) {
            completions.push({
              label: datasetId,
              type: "namespace",
              info: `Fields from ${datasetId} dataset`,
              apply: `${datasetId}.`,
            });
          }
        });
      }

      // Add "signals" for alert, opportunity, and recommendation types
      if (
        (type === "alert" ||
          type === "opportunity" ||
          type === "recommendation") &&
        "signals".startsWith(text)
      ) {
        completions.push({
          label: "signals",
          type: "namespace",
          info: "Computed signal values",
          apply: "signals.",
        });
      }
    }

    if (completions.length === 0) return null;

    return {
      from: word.from,
      options: completions,
    };
  };
}

/**
 * ExpressionEditor - Reusable CodeMirror-based Python expression editor
 * with intelligent autocomplete for metrics, signals, and keywords.
 */
export function ExpressionEditor({
  value,
  onChange,
  type,
  selectedDatasets = [],
  availableDatasets = {},
  availableSignals = [],
  placeholder = "",
  minHeight = "200px",
}: ExpressionEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);

  useEffect(() => {
    if (!editorRef.current) return;

    // Python syntax highlighting theme
    const pythonHighlightStyle = HighlightStyle.define([
      { tag: tags.keyword, color: "#c678dd" },
      { tag: tags.operator, color: "#56b6c2" },
      { tag: tags.string, color: "#98c379" },
      { tag: tags.number, color: "#d19a66" },
      { tag: tags.bool, color: "#d19a66" },
      { tag: tags.null, color: "#d19a66" },
      { tag: tags.comment, color: "#5c6370", fontStyle: "italic" },
      { tag: tags.function(tags.variableName), color: "#61afef" },
      { tag: tags.variableName, color: "#e06c75" },
      { tag: tags.propertyName, color: "#e06c75" },
      { tag: tags.bracket, color: "#abb2bf" },
      { tag: tags.brace, color: "#abb2bf" },
    ]);

    const customTheme = EditorView.theme({
      "&": {
        fontSize: "13px",
        backgroundColor: "hsl(var(--input-background))",
        border: "1px solid hsl(var(--border))",
        borderRadius: "6px",
        color: "hsl(var(--foreground))",
      },
      ".cm-content": {
        fontFamily: '"Fira Code", "Fira Mono", monospace',
        padding: "12px",
        minHeight: minHeight,
        caretColor: "hsl(var(--foreground))",
      },
      ".cm-line": {
        padding: "0",
        lineHeight: "1.6",
      },
      "&.cm-focused": {
        outline: "2px solid hsl(var(--ring))",
        outlineOffset: "2px",
      },
      ".cm-scroller": {
        overflow: "auto",
      },
      ".cm-cursor": {
        borderLeftColor: "hsl(var(--foreground))",
      },
      ".cm-selectionBackground, ::selection": {
        backgroundColor: "hsl(var(--accent))",
      },
      "&.cm-focused .cm-selectionBackground": {
        backgroundColor: "hsl(var(--accent))",
      },
      ".cm-activeLine": {
        backgroundColor: "hsl(var(--muted) / 0.3)",
      },
      ".cm-gutters": {
        display: "none",
      },
      ".cm-placeholder": {
        color: "hsl(var(--muted-foreground))",
      },
    });

    const autocompleteExtension = autocompletion({
      override: [
        minimalPythonKeywords,
        localCompletionSource,
        createAutocompleteFunction(
          type,
          selectedDatasets,
          availableDatasets,
          availableSignals,
        ),
      ],
      activateOnTyping: true,
      maxRenderedOptions: 10,
      defaultKeymap: true,
    });

    const extensions: Extension[] = [
      python(),
      syntaxHighlighting(pythonHighlightStyle),
      keymap.of([
        {
          key: "Tab",
          run: (view) => {
            // First try to accept completion
            const accepted = acceptCompletion(view);
            if (accepted) {
              // Check if the text just before the cursor ends with "."
              const pos = view.state.selection.main.head;
              const textBefore = view.state.doc.sliceString(
                Math.max(0, pos - 50),
                pos,
              );

              // If we just completed something ending with ".", trigger autocomplete again
              if (textBefore.match(/[a-z_]+\.$/)) {
                setTimeout(() => {
                  startCompletion(view);
                }, 10);
              }
              return true;
            }
            return false;
          },
        },
        ...defaultKeymap,
        ...historyKeymap,
        ...closeBracketsKeymap,
      ]),
      autocompleteExtension,
      customTheme,
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          onChange(update.state.doc.toString());
        }
      }),
      EditorView.lineWrapping,
      drawSelection(),
      highlightActiveLine(),
      highlightActiveLineGutter(),
      history(),
      closeBrackets(),
    ];

    if (placeholder) {
      extensions.push(placeholderExtension(placeholder));
    }

    const startState = EditorState.create({
      doc: value,
      extensions,
    });

    const view = new EditorView({
      state: startState,
      parent: editorRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editor rebuilds only on structural changes; value/onChange sync is handled by the dedicated effects below
  }, [type, selectedDatasets.join(","), minHeight]);

  // Handle external value changes
  useEffect(() => {
    if (viewRef.current) {
      const currentValue = viewRef.current.state.doc.toString();
      if (value !== currentValue) {
        viewRef.current.dispatch({
          changes: { from: 0, to: currentValue.length, insert: value },
        });
      }
    }
  }, [value]);

  return <div ref={editorRef} />;
}
